import React, { useState, useRef, useEffect } from 'react';
import DisplayDataMetrics from './components/ui/displayDataMetrics.js';
import InitializeMap from './components/core/mapboxInit.js';
import { useGlobalRefsConverting, useGlobalStateConverting } from './components/core/globalVariablesConverting.js';
import * as turf from '@turf/turf';
import harrierPolygonLayer from '../../components/core/harrierPolygonLayer.js';
import { center } from '@turf/turf';
import { bbox } from '@turf/turf';  // Bounding box function from Turf

import { fmesh, fullmesh } from '../../components/core/globalVariables.js';
import ResetMap from '../../components/core/resetMap.js';

const MetricSelection = ({
    geojson,
    globalRefs,
    globalState,
}) => {

    const newGeojsonRef = useRef(null);

    const globalRefsConverting = useGlobalRefsConverting();
    const globalStateConverting = useGlobalStateConverting();

    const mapRef = globalRefs.mapRef;
    const polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
    const setConvertingData = globalState.setConvertingData;
    const setIsViewingConvertedData = globalState.setIsViewingConvertedData;
    let setUniversalParcelDifferentiator = globalState.setUniversalParcelDifferentiator;
    let setUniversalColorPaletteName = globalState.setUniversalColorPaletteName;
    let centroidRef = globalRefs.centroidRef;
    let originalGeoJSONforComparingRef = globalRefs.originalGeoJSONforComparingRef;

    let layers = globalState.layers;
    let setLayers = globalState.setLayers;

    let i = 0;

    const [uniqueKeys, setUniqueKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [metricsInitiallySelected, setMetricsInitiallySelected] = useState('false');
    const [allProperties, setAllProperties] = useState([]);
    const [selectedPalette, setSelectedPalette] = useState(null);

    let longstandingGeoJSONRef = globalRefsConverting.longstandingGeoJSONRef;
    let setMapLoadedData = globalStateConverting.setMapLoadedData;
    let mapLoadedData = globalStateConverting.mapLoadedData;
    let parcelDifferentiator = globalStateConverting.parcelDifferentiator;
    let setParcelDifferentiator = globalStateConverting.setParcelDifferentiator;
    let colorPaletteName = globalStateConverting.colorPaletteName;
    let setColorPaletteName = globalStateConverting.setColorPaletteName;

    const initPaletteSet = useRef(false);

    // 

    let longstandingGeoJSONRefGlobal = globalRefs.longstandingGeoJSONRef;
    let originalGeoJSONforComparingRefGlobal = globalRefs.originalGeoJSONforComparingRef;

    //

    useEffect(() => {
        if (mapLoadedData) {
            InitializeMap(globalRefsConverting, globalStateConverting);
        }
    }, [mapLoadedData]);


    // ALLOCATING METRICS FUNCTIONS - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    useEffect(() => {
        if (geojson && geojson.features) {
            for (let i = 0; i < geojson.features.length; i++) {
                let feature = geojson.features[i];
                for (let key in feature) {
                    if (feature.hasOwnProperty(key)) {
                        setUniqueKeys((prevKeys) => {
                            if (!prevKeys.includes(key)) {
                                return [...prevKeys, key];
                            }
                            return prevKeys;
                        });
                    }
                }
            }
        }
    }, [geojson]);

    // CREATE NEW GEOJSON FUNCTIONS - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    useEffect(() => {
        if (geojson)
            createNewGeojson();
    }, [geojson]);

    const createNewGeojson = () => {

        const lastLayerNumber = layers.length > 0
            ? Math.max(...layers.map(layer => layer.number))
            : 0;
        const nextLayerNumber = lastLayerNumber + 1;

        const newGeojson = {
            type: 'FeatureCollection',
            features: geojson.features.map((feature) => {
                let newFeature = {
                    type: 'Feature',
                    geometry: feature.geometry,
                    properties: {} // Initialize properties as an empty object
                };
                // Function to merge all keys from nested objects into newFeature.properties
                const mergeNestedProperties = (source) => {
                    if (source && typeof source === 'object') {
                        Object.keys(source).forEach((key) => {
                            if (typeof source[key] === 'object' && source[key] !== null) {
                                Object.keys(source[key]).forEach((nestedKey) => {
                                    newFeature.properties[nestedKey] = source[key][nestedKey];
                                });
                            }
                        });
                    }
                };
                // Merge keys dynamically from nested properties and attributes
                mergeNestedProperties(feature); // This will only include keys from nested objects
                return newFeature;
            })
        };
        for (let i = 0; i < newGeojson.features.length; i++) {
            let feature = newGeojson.features[i];
            let length = turf.length(feature, { units: 'kilometers' });
            let area = turf.area(feature);
            feature.feature_data_standard = {
                created: "2024-01-01T00:00:00+00:00",
                deleted: null,
                layer: nextLayerNumber  // Convert number to string
            };
            feature.feature_data_standard_calculated = {
                Shape_Length: length,
                Shape_Area: area,
            };
            feature.skylark_id = generateUUID();
            feature.external_ids = {
                searchlight_property_id: Math.floor(Math.random() * 10000).toString(),
                OBJECTID: Math.floor(Math.random() * 10000).toString(),
                OID_: Math.floor(Math.random() * 10000).toString(),
                id: Math.floor(Math.random() * 10000).toString(),
            };
        }
        convertToTimeSeries(newGeojson);
        longstandingGeoJSONRef.current = newGeojson;
        newGeojsonRef.current = newGeojson; // Update the ref
        const properties = getAllPropertiesForInnerKey(newGeojson);
        setAllProperties(properties);
        setMetricsInitiallySelected('true');
        setMapLoadedData(null);
        setColorPaletteName('land');
        setTimeout(() => {
            setLayers((prevLayers) => [...prevLayers, { number: prevLayers.length + 1 }]);
            setMapLoadedData(longstandingGeoJSONRef.current);
            createHarrierMap();
        }, 0);
    };

    // Helper function to generate a UUID
    const generateUUID = () => {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
    }

    // CONVERT TO TIME SERIES FUNCTIONS - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const convertToTimeSeries = (newGeojson) => {
        newGeojson.features.forEach((feature) => {
            feature.feature_data_simple_timeseries = {};
            Object.keys(feature.properties).forEach((key) => {
                feature.feature_data_simple_timeseries[key] = {
                    '2024-01-01T00:00:00+00:00': feature.properties[key] || null,
                    '2027-01-01T00:00:00+00:00': feature.properties[key] || null,
                    '2029-01-01T00:00:00+00:00': feature.properties[key] || null,
                    '2031-01-01T00:00:00+00:00': feature.properties[key] || null
                };
                feature.feature_data_simple_timeseries.BoundaryChange = {
                    '2024-01-01T00:00:00+00:00': "000,000",
                    '2027-01-01T00:00:00+00:00': "000,000",
                    '2029-01-01T00:00:00+00:00': "000,000",
                    '2031-01-01T00:00:00+00:00': "000,000"
                };
                feature.feature_data_simple_timeseries.Value = {
                    '2024-01-01T00:00:00+00:00': +(Math.random() * 1000000).toFixed(2),
                    '2027-01-01T00:00:00+00:00': +(Math.random() * 1000000).toFixed(2),
                    '2029-01-01T00:00:00+00:00': +(Math.random() * 1000000).toFixed(2),
                    '2031-01-01T00:00:00+00:00': +(Math.random() * 1000000).toFixed(2)
                };
                feature.feature_data_simple_timeseries.Class = {
                    "2024-01-01T00:00:00+00:00": +(Math.random() * 10).toFixed(2),
                    "2027-01-01T00:00:00+00:00": +(Math.random() * 10).toFixed(2),
                    "2029-01-01T00:00:00+00:00": +(Math.random() * 10).toFixed(2),
                    "2031-01-01T00:00:00+00:00": +(Math.random() * 10).toFixed(2)
                };                
            });
        });
    }

    // ALLOCATING PROPERTIES FUNCTIONS - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const getAllPropertiesForInnerKey = (geojson) => {
        let allProperties = [];
        geojson.features.forEach((feature) => {
            let properties = feature.feature_data_simple_timeseries;
            if (properties) {
                Object.keys(properties).forEach((key) => {
                    if (!allProperties.includes(key)) {
                        allProperties.push(key);
                    }
                });
            }
        });
        return allProperties;
    };

    const downloadGeoJSON = (geojson) => {
        const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(geojson));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute('href', dataStr);
        downloadAnchorNode.setAttribute('download', 'harrier_map.geojson');
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    // CREATE HARRIER MAP FUNCTION - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const createHarrierMap = () => {
        const newGeojsontoView = newGeojsonRef.current;
        downloadGeoJSON(newGeojsontoView);
        // const customCentroid = center(newGeojsontoView);
        // centroidRef.current = customCentroid;

        longstandingGeoJSONRefGlobal.current.features.push(...newGeojsontoView.features);

        originalGeoJSONforComparingRefGlobal.current.features.push(...newGeojsontoView.features);
        
        try {
            setConvertingData(false);
            setIsViewingConvertedData(true);
            setUniversalParcelDifferentiator(parcelDifferentiator);
            setUniversalColorPaletteName(colorPaletteName);
        } catch (error) {
            console.error('Error creating Harrier map:', error);
        }

        ResetMap(globalRefs, globalState);
    };

    return (
        <div>

            <div className='fileInterfacecontainer'>

                <div className='rightColumn'>
                    <DisplayDataMetrics globalRefsConverting={globalRefsConverting} globalStateConverting={globalStateConverting} />
                </div>
            </div>

        </div>
    );
}

export default MetricSelection;
